import React, { useCallback, useEffect, useState } from 'react';
import { X, AlertTriangle, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { saleInvoicesAPI } from '../../services/api';
import { formatAmount, formatNumber } from '../../utils/currency';
import './EInvoiceConfigModal.css';

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

function previousMonth() {
  const now = new Date();
  now.setDate(1);
  now.setMonth(now.getMonth() - 1);
  return { annee: now.getFullYear(), mois: now.getMonth() + 1 };
}

export default function GenerateFromTimesheetsModal({ project, entities, onClose, onCreated }) {
  const initial = previousMonth();
  const [annee, setAnnee] = useState(initial.annee);
  const [mois, setMois] = useState(initial.mois);
  const [onlyValidated, setOnlyValidated] = useState(true);
  const [entityId, setEntityId] = useState('');
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (entityId || !entities?.length) return;
    const agency = (project?.agence || '').trim().toUpperCase();
    const preferred = entities.find((e) => e.code?.toUpperCase() === agency) || entities[0];
    setEntityId(preferred.id);
  }, [entities, entityId, project]);

  const entity = entities?.find((e) => e.id === entityId);
  const currency = entity?.default_currency || 'EUR';

  const loadProposal = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await saleInvoicesAPI.billingProposal(project.id, annee, mois, onlyValidated);
      setProposal(data);
    } catch {
      toast.error('Impossible de calculer la proposition de facturation');
    } finally {
      setLoading(false);
    }
  }, [project.id, annee, mois, onlyValidated]);

  useEffect(() => { loadProposal(); }, [loadProposal]);

  const billable = (proposal?.lines || []).filter((l) => l.pu !== null && l.quantite > 0);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const { data } = await saleInvoicesAPI.createFromTimesheets({
        project_id: project.id,
        annee,
        mois,
        only_validated: onlyValidated,
        legal_entity_id: entityId || null,
        date_facture: new Date(annee, mois, 0).toISOString().slice(0, 10),
      });
      toast.success(`Facture brouillon créée (${data.ref})`);
      onCreated?.(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.message || detail || 'Création impossible');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="eic-overlay" onClick={onClose}>
      <div className="eic-modal" onClick={(e) => e.stopPropagation()}>
        <header className="eic-header">
          <div>
            <h2>Générer une facture depuis les feuilles de temps</h2>
            <p className="eic-sub">{project?.nom}</p>
          </div>
          <button className="eic-close" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
        </header>

        <div className="eic-body">
          <div className="eic-grid" style={{ marginBottom: 18 }}>
            <div className="eic-field">
              <label>Mois</label>
              <select value={mois} onChange={(e) => setMois(Number(e.target.value))}>
                {MONTHS.map((label, index) => (
                  <option key={label} value={index + 1}>{label}</option>
                ))}
              </select>
            </div>
            <div className="eic-field">
              <label>Année</label>
              <input type="number" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} />
            </div>
            <div className="eic-field">
              <label>Entité émettrice</label>
              <select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
                {(entities || []).map((e) => (
                  <option key={e.id} value={e.id}>{e.code} — {e.default_currency}</option>
                ))}
              </select>
            </div>
            <div className="eic-field">
              <label>Feuilles de temps</label>
              <select value={onlyValidated ? 'validated' : 'all'} onChange={(e) => setOnlyValidated(e.target.value === 'validated')}>
                <option value="validated">Validées uniquement</option>
                <option value="all">Toutes (aperçu)</option>
              </select>
            </div>
          </div>

          {proposal?.warnings?.length > 0 && (
            <div className="eic-alert">
              <AlertTriangle size={14} />
              <div>
                <strong>{proposal.warnings.length} avertissement(s)</strong>
                {proposal.warnings.map((w) => <div key={w} className="eic-muted">{w}</div>)}
              </div>
            </div>
          )}

          {loading ? (
            <p className="eic-muted">Calcul en cours…</p>
          ) : billable.length === 0 ? (
            <p className="eic-muted">
              Aucun jour facturable pour {MONTHS[mois - 1]} {annee}.
              Vérifiez que les feuilles de temps sont validées et rattachées à ce projet.
            </p>
          ) : (
            <table className="pd-table">
              <thead>
                <tr>
                  <th>Ressource</th>
                  <th style={{ textAlign: 'right' }}>Jours</th>
                  <th style={{ textAlign: 'right' }}>TJM</th>
                  <th style={{ textAlign: 'right' }}>Total HT</th>
                </tr>
              </thead>
              <tbody>
                {billable.map((line) => (
                  <tr key={line.user_id}>
                    <td>
                      {line.resource_nom}
                      {line.poste && <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{line.poste}</div>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatNumber(line.quantite, 2)}</td>
                    <td style={{ textAlign: 'right' }}>{formatAmount(line.pu, currency)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(line.total_ht, currency)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 600 }}>Total</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatNumber(proposal.total_jours, 2)}</td>
                  <td />
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(proposal.total_ht, currency)}</td>
                </tr>
              </tfoot>
            </table>
          )}

          <p className="eic-muted" style={{ marginTop: 14 }}>
            Jours comptabilisés : activité et télétravail, plafonnés à 1 jour par date pour éviter les doublons.
          </p>
        </div>

        <footer className="eic-footer">
          <button className="eic-btn" onClick={onClose}>Annuler</button>
          <button className="eic-btn primary" onClick={handleCreate} disabled={creating || billable.length === 0}>
            <Check size={14} /> {creating ? 'Création…' : 'Créer la facture brouillon'}
          </button>
        </footer>
      </div>
    </div>
  );
}
